import React, { useState, useEffect, useCallback, useMemo, useContext } from 'react';
import { useNavigate } from 'react-router-dom'; // Import du hook useNavigate
import { AuthContext } from './AuthContext'; // Import du contexte d'authentification
// Import du logo (ajouté pour garantir son bon fonctionnement)
import logo from './gon-life-logo.png';

// Fonction pour générer un token d'authentification
const generateAuthToken = (username) => {
  // Création d'un timestamp
  const timestamp = Date.now();
  
  // Combinaison du nom d'utilisateur et du timestamp
  const data = `${username}-${timestamp}`;
  
  // Simple encodage en base64
  return btoa(data);
};

// Composant d'animation séquentielle amélioré avec zones d'exclusion
const SequentialWords = () => {
  const [visibleWords, setVisibleWords] = useState([]);
  
  // Liste des activités à afficher (mémorisée) avec ajout des nouveaux mots demandés
  const activities = useMemo(() => [
    "Performance des bâtiments",
    "Mesures de confort",
    "Mesures acoustiques",
    "Qualité de l'eau",
    "Qualité de l'air",
    "Conseil & accompagnement",
    "Mesures des OEM",
    "Mesures thermiques",
    "Référentiel HQE",
    "Référentiel BREEAM",
    "Mesures d'éclairement",
    "Référentiel WELL",
    "Label OsmoZ",
    "Référentiel LEED",
    "Monitoring",
    "Surveillance environnementale"
  ], []);
  
  // Zones sûres bien en dehors de la fenêtre de connexion centrale
  // Positions ajustées pour éviter tout débordement hors de l'écran
  const safeZones = useMemo(() => [
    // Zone supérieure - plus éloignée des bords
    { top: '12%', left: '25%' },
    { top: '12%', left: '50%' },
    { top: '12%', left: '75%' },
    
    // Zone inférieure - plus éloignée des bords
    { top: '88%', left: '25%' },
    { top: '88%', left: '50%' },
    { top: '88%', left: '75%' },
    
    // Zone gauche - plus éloignée des bords
    { top: '30%', left: '15%' },
    { top: '50%', left: '15%' },
    { top: '70%', left: '15%' },
    
    // Zone droite - plus éloignée des bords
    { top: '30%', left: '85%' },
    { top: '50%', left: '85%' },
    { top: '70%', left: '85%' }
  ], []);
  
  // Fonction pour déterminer la taille de police appropriée en fonction de la longueur du texte
  // Tailles réduites pour compenser l'effet du gras qui augmente visuellement la taille
  const getAppropriateSize = useCallback((text) => {
    const length = text.length;
    
    if (length < 6) {
      return "text-4xl"; // Réduit d'une taille pour compenser le gras
    } else if (length < 10) {
      return "text-3xl"; // Réduit d'une taille pour compenser le gras
    } else if (length < 15) {
      return "text-2xl"; // Réduit d'une taille pour compenser le gras
    } else if (length < 25) {
      return "text-xl"; // Réduit d'une taille pour compenser le gras
    } else {
      return "text-lg"; // Nouvelle taille plus petite pour les très longs textes
    }
  }, []);
  
  // Fonction pour vérifier si une position est appropriée pour un mot donné
  // en tenant compte de la longueur du mot
  const isPositionSafeForWord = useCallback((word, position) => {
    const length = word.length;
    
    // Pour les mots très longs, éviter certaines positions près des bords
    if (length > 20 && (position.left === '15%' || position.left === '85%')) {
      return false;
    }
    
    // Pour les mots longs, éviter les positions extrêmes
    if (length > 15 && (position.left === '15%' || position.left === '85%')) {
      return false;
    }
    
    return true;
  }, []);
  
  // Fonction pour ajouter un nouveau mot (optimisée avec useCallback)
  const addNewWord = useCallback(() => {
    // Limiter à 4 mots à la fois
    setVisibleWords(prev => {
      if (prev.length >= 4) return prev;
      
      // Choisir un mot aléatoire qui n'est pas déjà affiché
      const availableWords = activities.filter(
        word => !prev.some(item => item.text === word)
      );
      
      if (availableWords.length === 0) return prev;
      
      // Choisir une position non utilisée
      const availablePositions = safeZones.filter(
        pos => !prev.some(item => item.position.top === pos.top && item.position.left === pos.left)
      );
      
      if (availablePositions.length === 0) return prev;
      
      // Essayer jusqu'à 10 fois de trouver une combinaison mot/position qui fonctionne
      for (let attempt = 0; attempt < 10; attempt++) {
        // Sélection aléatoire d'un mot et d'une position
        const randomWordIndex = Math.floor(Math.random() * availableWords.length);
        const randomPositionIndex = Math.floor(Math.random() * availablePositions.length);
        
        const selectedWord = availableWords[randomWordIndex];
        const selectedPosition = availablePositions[randomPositionIndex];
        
        // Vérifier si cette position est appropriée pour ce mot
        if (isPositionSafeForWord(selectedWord, selectedPosition)) {
          // Déterminer la taille en fonction de la longueur du mot
          const wordSize = getAppropriateSize(selectedWord);
          
          const newWord = {
            id: Date.now(),
            text: selectedWord,
            position: selectedPosition,
            size: wordSize,
            startTime: Date.now()
          };
          
          return [...prev, newWord];
        }
      }
      
      // Si aucune combinaison ne fonctionne après 10 tentatives, ne pas ajouter de mot
      return prev;
    });
  }, [activities, safeZones, getAppropriateSize, isPositionSafeForWord]);
  
  // Fonction pour mettre à jour l'état des mots (optimisée)
  const updateWords = useCallback(() => {
    const now = Date.now();
    
    setVisibleWords(prev => {
      // Si rien à mettre à jour, éviter un re-render inutile
      if (prev.length === 0) return prev;
      
      return prev
        .filter(word => {
          const timeElapsed = now - word.startTime;
          return timeElapsed < 9000; // Total de l'animation: 3s apparition + 5s visibilité + 1s disparition
        });
    });
  }, []);
  
  // Démarrer l'animation avec une fréquence de mise à jour optimisée
  useEffect(() => {
    // Ajouter un nouveau mot toutes les 2-3 secondes
    const addInterval = setInterval(() => {
      // Ajouter un nouveau mot si moins de 4 sont affichés
      if (Math.random() > 0.3 || visibleWords.length === 0) {
        addNewWord();
      }
    }, 2000);
    
    // Mettre à jour/supprimer les mots toutes les 1000ms
    const updateInterval = setInterval(updateWords, 1000);
    
    return () => {
      clearInterval(addInterval);
      clearInterval(updateInterval);
    };
  }, [addNewWord, updateWords, visibleWords.length]);
  
  return (
    <div className="fixed inset-0 overflow-hidden select-none pointer-events-none">
      {/* Style global pour les animations CSS */}
      <style jsx global>{`
        @keyframes appear {
          0% { opacity: 0; filter: blur(8px); }
          100% { opacity: 1; filter: blur(0px); }
        }
        
        @keyframes disappear {
          0% { opacity: 1; filter: blur(0px); }
          100% { opacity: 0; filter: blur(8px); }
        }
        
        .word-appear {
          animation: appear 3s ease-in-out forwards;
        }
        
        .word-visible {
          opacity: 1;
          filter: blur(0px);
        }
        
        .word-disappear {
          animation: disappear 1s ease-in-out forwards;
        }
        
        /* Police stylisée et professionnelle - maintenant en gras */
        .professional-font {
          font-family: 'Montserrat', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif;
          font-weight: 600;
          letter-spacing: 0.05em;
        }
      `}</style>
      
      {visibleWords.map(word => {
        const timeElapsed = Date.now() - word.startTime;
        let animationClass = '';
        
        if (timeElapsed < 3000) {
          animationClass = 'word-appear';
        } else if (timeElapsed < 8000) {
          animationClass = 'word-visible';
        } else {
          animationClass = 'word-disappear';
        }
        
        return (
          <div
            key={word.id}
            className={`absolute text-blue-700 ${word.size} ${animationClass} professional-font`}
            style={{
              top: word.position.top,
              left: word.position.left,
              transform: 'translate(-50%, -50%)',
              // S'assurer que les mots restent dans les limites
              maxWidth: '70vw',
              textAlign: 'center',
              overflowWrap: 'break-word',
              hyphens: 'auto'
            }}
          >
            {word.text}
          </div>
        );
      })}
    </div>
  );
};

// Composant pour les liens sociaux avec icônes
const SocialLinks = () => {
  return (
    <div className="flex justify-center space-x-6 mt-4">
      {/* Lien vers le site web */}
      <a 
        href="https://www.g-on.life/" 
        target="_blank" 
        rel="noopener noreferrer"
        className="text-gray-600 hover:text-blue-600 transition-colors duration-300 flex items-center"
      >
        <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
          <path fillRule="evenodd" d="M10 0a10 10 0 1 0 10 10A10 10 0 0 0 10 0zm6.2 4.2a8.21 8.21 0 0 1 1.9 5.3 10.47 10.47 0 0 0-5.9-.3l-.6-1.4A11.94 11.94 0 0 0 16.2 4.2zM10 1.8a8.22 8.22 0 0 1 5.5 2.1 9.73 9.73 0 0 1-4.1 3l-.7-1.4a.4.4 0 0 0-.7-.1L7.6 7.9l-2.7-.4a.4.4 0 0 0-.4.6l.8 1.2-2.3 2.2a.5.5 0 0 0 0 .6l.9.9a.5.5 0 0 0 .7 0l2.2-2.2 1.2.8a.4.4 0 0 0 .6-.3l-.5-2.7 2.5-2.5a.4.4 0 0 0-.1-.7l-1.3-.6a8.4 8.4 0 0 1 .8-2zm-8.2 2.4a8.23 8.23 0 0 1 5.3-1.9l.4.9c-1.8.1-4.1.5-6.5 1.9l.8-.9zm-1 9.4a8.27 8.27 0 0 1 3.5-7.5l1.6.3L5.6 8a.4.4 0 0 0-.2.5l.8 2.5-1.4 1.3a2.15 2.15 0 0 0 0 3.1l.9.9a2.23 2.23 0 0 0 3.1 0l1.3-1.4 2.5.8a.4.4 0 0 0 .5-.2l1.5-4-1.4-3.2a8.87 8.87 0 0 1 6.9.4 8.2 8.2 0 0 1-10.8 8.4z" clipRule="evenodd" />
        </svg>
        <span className="hidden sm:inline">Site Web</span>
      </a>
      
      {/* Lien vers LinkedIn */}
      <a 
        href="https://www.linkedin.com/company/g-on-life/posts/?feedView=all" 
        target="_blank" 
        rel="noopener noreferrer"
        className="text-gray-600 hover:text-blue-600 transition-colors duration-300 flex items-center"
      >
        <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
          <path fillRule="evenodd" d="M16.338 16.338H13.67V12.16c0-1-.02-2.278-1.39-2.278-1.39 0-1.6 1.084-1.6 2.205v4.25H8.014v-8.59h2.56v1.174h.037c.356-.675 1.227-1.387 2.524-1.387 2.7 0 3.203 1.778 3.203 4.092v4.71zM5.005 6.575a1.548 1.548 0 11-.003-3.096 1.548 1.548 0 01.003 3.096zm-1.337 9.763H6.34v-8.59H3.667v8.59zM17.668 1H2.328C1.595 1 1 1.581 1 2.298v15.403C1 18.418 1.595 19 2.328 19h15.34c.734 0 1.332-.582 1.332-1.299V2.298C19 1.581 18.402 1 17.668 1z" clipRule="evenodd" />
        </svg>
        <span className="hidden sm:inline">LinkedIn</span>
      </a>
    </div>
  );
};

const EnhancedLoginPage = ({ onLogin }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate(); // Hook pour la navigation
  const { login } = useContext(AuthContext); // Accès au contexte d'auth

  // Liste des identifiants valides avec leurs affaires associées
  const validCredentials = {
'paris-test-2025': { 
      password: 'mdp', 
      affaireId: 'paris-test-2025' // ID de l'affaire associée à cet utilisateur
    },
'paris-mars-2025': { 
      password: 'G-ON', 
      affaireId: 'paris-mars-2025' // ID de l'affaire associée à cet utilisateur
    },
    'fourchambault-mars-2025': { 
      password: '7gen0man', 
      affaireId: 'fourchambault-mars-2025' // ID de l'affaire associée à cet utilisateur
    },
    'fourchambault-avril-2025': { 
      password: '7gen0man', 
      affaireId: 'fourchambault-avril-2025'
    },
    'fourchambault-mai-2025': { 
      password: '7gen0man', 
      affaireId: 'fourchambault-mai-2025'
    },
    'uxco_ecla-ete-2025': { 
      password: '1234', 
      affaireId: 'uxco_ecla-ete-2025'
    },
    'uxco_ecla-ete-2025_maintenance': { 
      password: '5678', 
      affaireId: 'uxco_ecla-ete-2025_maintenance'
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    // Simuler un délai réseau pour l'UX
    setTimeout(() => {
      const userCredentials = validCredentials[username];
      
      if (userCredentials && userCredentials.password === password) {
        // Stocker l'état d'authentification dans localStorage
        localStorage.setItem('isAuthenticated', 'true');
        // Date d'expiration (24h)
        const expiry = new Date();
        expiry.setHours(expiry.getHours() + 24);
        localStorage.setItem('authExpiry', expiry.toISOString());
        
        // AJOUT: Créer un token d'authentification et le stocker dans un cookie
        const token = generateAuthToken(username);
document.cookie = `gon_auth_token=${token}; path=/; max-age=86400; SameSite=Lax`;
        
        // Stocker l'ID du rapport actuel
        localStorage.setItem('currentReport', userCredentials.affaireId);
        
        // Appeler le callback de connexion réussie
        login(username);
        
        // Rediriger vers le gestionnaire de redirection avec l'ID d'affaire
        // Correction : extraire dynamiquement le dossier et la sous-répertoire
        const parts = userCredentials.affaireId.split('-'); 
        const region = parts[0]; // "paris" ou "fourchambault"
        const monthYear = `${parts[1]}-${parts[2]}`; // "septembre-2024" ou "mars-2025"
        const reportURL = `/reports/${region}/${monthYear}/`;
        window.location.href = reportURL;
      } else {
        setError('Identifiant ou mot de passe incorrect');
      }
      setIsLoading(false);
    }, 800);
  };

  // Précharger la police Montserrat pour avoir un style professionnel et épuré
  useEffect(() => {
    // Vérifier si la police n'est pas déjà chargée
    if (!document.getElementById('montserrat-font')) {
      const link = document.createElement('link');
      link.id = 'montserrat-font';
      link.rel = 'stylesheet';
      // Ajout des variantes de poids 600 (semi-bold) pour le texte en gras
      link.href = 'https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;600&display=swap';
      document.head.appendChild(link);
    }
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-50 relative overflow-hidden">
      {/* Animation des mots qui apparaissent/disparaissent */}
      <SequentialWords />
      
      {/* Effet de verre dépoli */}
      <div className="max-w-md w-full p-8 rounded-xl shadow-lg backdrop-blur-sm bg-white/90 border border-white/20 relative z-10 transition-all duration-300 hover:shadow-xl">
        <div className="flex justify-center mb-6">
          {/* Utiliser l'image originale avec plusieurs approches pour assurer qu'elle s'affiche bien */}
          <img 
            src={logo || "/gon-life-logo.png"} 
            alt="G-ON Life" 
            className="h-16 object-contain"
            onError={(e) => {
              // Fallback si l'image n'est pas trouvée
              e.target.src = "/gon-life-logo.png";
              // Essayer avec un chemin relatif si le précédent ne fonctionne pas
              e.target.onerror = () => {
                e.target.src = "./gon-life-logo.png";
                // Dernier recours - chemin absolu
                e.target.onerror = () => {
                  e.target.src = `${window.location.origin}/gon-life-logo.png`;
                  e.target.onerror = null;
                };
              };
            }}
          />
        </div>
        
        <h2 className="text-2xl font-semibold text-center text-gray-800 mb-4 professional-font">
          Plateforme de visualisation des données
        </h2>
        
        <p className="text-center text-gray-600 mb-6 professional-font">
          Veuillez vous authentifier pour accéder aux données
        </p>
        
        {error && (
          <div className="bg-red-50 border-l-4 border-red-500 text-red-700 p-4 rounded mb-4 shadow-sm">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-500" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3 professional-font">
                {error}
              </div>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-gray-700 text-sm font-medium mb-2 professional-font" htmlFor="username">
              Identifiant
            </label>
            <input
              id="username"
              type="text"
              className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors professional-font"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
            />
          </div>
          
          <div>
            <label className="block text-gray-700 text-sm font-medium mb-2 professional-font" htmlFor="password">
              Mot de passe
            </label>
            {/* Lien "Mot de passe oublié" commenté pour ne pas apparaître temporairement
            <a href="#" className="text-sm text-blue-600 hover:text-blue-800 transition-colors professional-font">
              Mot de passe oublié ?
            </a>
            */}
            <input
              id="password"
              type="password"
              className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors professional-font"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>
          
          <div>
            <button
              type="submit"
              className={`w-full bg-gradient-to-r from-blue-600 to-blue-700 text-white py-2 px-4 rounded-md hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 shadow-sm transition-all duration-300 transform hover:scale-[1.01] professional-font ${isLoading ? 'opacity-75 cursor-wait' : ''}`}
              disabled={isLoading}
            >
              {isLoading ? (
                <div className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span>Connexion en cours...</span>
                </div>
              ) : 'Se connecter'}
            </button>
          </div>
        </form>
        
        <div className="mt-6 text-center text-sm text-gray-600 border-t border-gray-200 pt-4">
          <p className="font-medium professional-font">Données confidentielles</p>
          <p className="mt-1 professional-font">© {new Date().getFullYear()} G-ON Life - Tous droits réservés</p>
          
          {/* Liens sociaux placés sous la mention "Tous droits réservés" */}
          <SocialLinks />
        </div>
      </div>
    </div>
  );
};

export default EnhancedLoginPage;